import Modal from '../Modal';
import { useEffect, useState } from 'react';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import {
    setCustomFieldsSchemaQuery,
    useCustomFieldsSchemaQuery,
} from '../../lib/Queries';
import SingleFieldSchema from './SingleFieldSchema.jsx';
import Loading from '../Loading';
import { ObjectTypeHumanNames } from '../../lib/ObjectTypes.js';
import { ucfirst } from '../../lib/Utils.js';
import { ChevronRightIcon } from '@heroicons/react/24/outline/index.js';
import SortableList from '../SortableList.jsx';
import useSaveCustomFieldSchema from '../../lib/useSaveCustomFieldSchema.jsx';
import IconSort from '../icons/IconSort.jsx';

const EditField = ({
    isOpen,
    onClose,
    schema,
    fieldSchema,
    fieldKey,
    type,
    onSave,
}) => {
    const [fieldData, setFieldData] = useState(fieldSchema || {});
    const { save, isSaving } = useSaveCustomFieldSchema(type);

    function submit(ev) {
        ev.preventDefault();

        if (!fieldKey) {
            // It's new!
            fieldKey = `field-${new Date().getTime()}`;
        }

        const item = {
            key: fieldKey,
            schema: fieldData,
        };

        const newSchema = [...schema];
        const idx = newSchema.findIndex(i => i.key === fieldKey);
        if (idx > -1) {
            newSchema[idx] = item;
        } else {
            newSchema.push(item);
        }

        save(newSchema).then(() => onSave());
    }

    function doDelete() {
        if (confirm('Are you sure you want to delete this field?')) {
            const newSchema = [...schema];
            const idx = newSchema.findIndex(i => i.key === fieldKey);
            newSchema.splice(idx, 1);
            save(newSchema).then(() => onSave());
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <SingleFieldSchema
                value={fieldData}
                setValue={setFieldData}
                type={type}
            />
            <div className="mt-4 flex flex-wrap gap-2 xs:flex-nowrap items-center justify-between">
                <div className="flex flex-wrap gap-2 xs:flex-nowrap w-full">
                    <button onClick={submit} type="submit" className="btn-save w-full xs:w-auto">
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        type="button"
                        className="btn-gray-pill-lg w-full xs:w-auto"
                        onClick={onSave}
                    >
                        Cancel
                    </button>
                </div>

                <button
                    type="button"
                    className="btn-pink-pill-lg w-full xs:w-auto"
                    onClick={() => doDelete()}
                >
                    Delete
                </button>
            </div>
        </Modal>
    );
};

const EditSchema = ({ type, className, typeName }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editingFieldKey, setEditingFieldKey] = useState(null);
    const { save, isSaving } = useSaveCustomFieldSchema(type);

    const { data, isLoading } = useCustomFieldsSchemaQuery(type);
    const schema = data || [];

    if (typeof schema.map !== 'function') {
        return null;
    }

    // Make sure it has an ID for the sortable list
    const schemaItems = schema.map(i => ({ ...i, id: i.key }));

    function onReorder(newItems) {
        save(newItems);
    }

    return (
        <div className={className}>
            {isLoading && <Loading />}

            <SortableList
                items={schemaItems}
                onReorder={onReorder}
                containerClassName="panel pl-8 divide-y"
                renderItem={({ item }) => (
                    <div
                        className="p-4 pl-0 group flex items-center justify-between cursor-pointer"
                        onClick={() => {
                            setIsEditing(true);
                            setEditingFieldKey(item.key);
                        }}
                    >
                        <div className="flex items-center">
                            <IconSort className="opacity-0  text-gray-500 group-hover:opacity-100 -ml-7 cursor-grab mr-1" />
                            <div>{item.schema.label}</div>
                        </div>
                        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                    </div>
                )}
            />

            <button
                type="button"
                className={`btn-light-pill ${schemaItems.length > 0 ? 'mt-4' : ''}`}
                onClick={() => {
                    setIsEditing(true);
                    setEditingFieldKey(null);
                }}
            >
                Add {typeName} Field
            </button>

            {isEditing && (
                <EditField
                    isOpen={isEditing}
                    onClose={() => setIsEditing(false)}
                    type={type}
                    fieldSchema={
                        editingFieldKey
                            ? schema.find(i => i.key === editingFieldKey).schema
                            : {}
                    }
                    fieldKey={editingFieldKey}
                    schema={schema}
                    onSave={() => setIsEditing(false)}
                />
            )}
        </div>
    );
};

export default EditSchema;
